<template>
	<Dropdown
		:shown="isVisible"
		:triggers="[]"
		:auto-hide="true"
		:distance="8"
		placement="bottom-start"
		theme="calendar-dropdown"
		@hide="isVisible = false"
	>
		<div :class="$style['d-range-picker']">
			<BaseRipple />
			<div
				:class="[
					$style['input'],
					alternative && $style['input--alternative']
				]"
				@click="isVisible = !isVisible"
			>
				<span>{{ displayName || $t("common.fields.select") }}</span>
				<FontAwesomeIcon :icon="['fal', 'calendar-alt']" size="sm" />
			</div>
		</div>

		<template #popper>
			<Calendar
				:interval="interval"
				:presets="presets"
				@change="handleChange"
				@preset-selected="handlePresetSelected"
			/>
		</template>
	</Dropdown>
</template>

<script>
import Calendar from './Calendar'
import { Dropdown } from 'floating-vue'
import { DateTime, Interval } from 'luxon'

export default {
	name: 'BaseDateRangePicker',
	components: {
		Calendar,
		Dropdown
	},
	props: {
		alternative: {
			type: Boolean,
			default: false
		},
		selected: {
			type: Object,
			default: null
		},
		ranges: {
			type: Array,
			default: null
		}
	},
	data () {
		const interval = this.buildInterval(this.selected)
		return {
			value: null,
			interval,
			isVisible: false
		}
	},
	computed: {
		displayName () {
			if (this.interval) {
				const range = {
					startDate: this.interval.start.startOf('day').toISO(),
					endDate: this.interval.end.endOf('day').toISO()
				}
				const presetMatch = this.presets.find(preset =>
					preset.range.startDate === range.startDate &&
					preset.range.endDate === range.endDate
				)
				return presetMatch ? presetMatch.name : this.value
			}
			return null
		},
		presets () {
			if (this.ranges) {
				return this.ranges
			}
			return [
				{
					name: this.$t('users-view.inner.statistics.filters.today'),
					id: 1,
					range: {
						startDate: DateTime.local().startOf('day').toISO(),
						endDate: DateTime.local().endOf('day').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.yesterday'),
					id: 2,
					range: {
						startDate: DateTime.local().minus({ days: 1 }).startOf('day').toISO(),
						endDate: DateTime.local().minus({ days: 1 }).endOf('day').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.this-week'),
					id: 3,
					range: {
						startDate: DateTime.local().startOf('week').toISO(),
						endDate: DateTime.local().endOf('day').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.last-week'),
					id: 4,
					range: {
						startDate: DateTime.local().minus({ days: 7 }).startOf('week').toISO(),
						endDate: DateTime.local().minus({ days: 7 }).endOf('week').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.current-month'),
					id: 5,
					range: {
						startDate: DateTime.local().startOf('month').toISO(),
						endDate: DateTime.local().endOf('day').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.last-month'),
					id: 6,
					range: {
						startDate: DateTime.local().minus({ months: 1 }).startOf('month').toISO(),
						endDate: DateTime.local().minus({ months: 1 }).endOf('month').toISO()
					}
				},
				{
					name: this.$t('users-view.inner.statistics.filters.current-year'),
					id: 7,
					range: {
						startDate: DateTime.local().startOf('year').toISO(),
						endDate: DateTime.local().endOf('day').toISO()
					}
				}
			]
		}
	},
	watch: {
		selected (val) {
			this.interval = this.buildInterval(val)
		}
	},
	methods: {
		buildInterval (selected) {
			if (selected && selected.luxonRange) {
				return Interval.fromDateTimes(selected.luxonRange.startDate, selected.luxonRange.endDate)
			} else if (selected && selected.start) {
				return selected
			}
			return Interval.fromDateTimes(DateTime.local().startOf('day'), DateTime.local().endOf('day'))
		},
		handleChange (interval) {
			if (interval) {
				this.interval = Interval.fromDateTimes(
					interval.start.startOf('day'),
					interval.end.endOf('day')
				)
				this.value = `${interval.start.toLocaleString()} - ${interval.end.toLocaleString()}`
			} else {
				this.interval = null
				this.value = null
			}
			this.$emit('change', this.interval)
		},
		handlePresetSelected (interval) {
			this.handleChange(interval)
			this.isVisible = false
		}
	}
}
</script>

<style lang="scss" module>
.d-range-picker {
    position: relative;
    // height: 36px;
    width: 240px;
    user-select: none;
}

.input {
    padding: $gutter $gutter * 2;
    position: relative;
    background-color: getColor(button-white);
    color: getColor(text);
    border-radius: 24px;
    box-shadow: 0 1px 2px 0 getColor(shadow-light);
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    border: 1px solid transparent;

    &:hover {
        cursor: pointer;
        opacity: 0.75;
    }

    // Dark mode: same border weight as button--white
    @include dark-mode {
        border-color: rgb(255, 255, 255, 0.14);
    }

    &__dropdown {
        border-radius: 24px;
        position: absolute;
        left: 0;
        right: 0;
        top: -2px;
    }

    &__dropdown-list {
        list-style: none;
        margin: 0;
        padding: $gutter $gutter * 2;
    }

    &__dropdown-list-item {
        padding: calc($gutter / 2) 0;
        margin: 0;

        &--active {
            cursor: pointer;
            font-weight: bold;
        }
    }
}
</style>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.v-popper--theme-calendar-dropdown {
    max-width: 100vw;
    box-sizing: border-box;

    .v-popper__inner {
        background: getColor(surface);
        border-radius: 6px;
        box-shadow: 0 3px 8px 0 getColor(shadow);
        padding: 0;
        overflow: visible;
        max-width: calc(100vw - 32px);
        box-sizing: border-box;

        // Dark mode: border on the floating popper panel
        @include dark-mode {
            border: 1px solid transparent;
            box-shadow: 0 3px 8px 0 getColor(shadow), 0 0 0 1px rgb(255, 255, 255, 0.10);
        }
    }

    .v-popper__arrow-container {
        display: none;
    }
}
</style>
